import React from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@common/components'
import { CenterContainer } from '@common/containers'
import penguinHeadPhoneImg from '@assets/images/mascot/penguin-headphone2.png'
import penguinPhoneImg from '@assets/images/mascot/penguin-pohne.png'
import {
  BpCheckedIcon,
  BpIcon,
  RadioCheckedIcon,
  RadioIcon,
  Steppper,
} from './collect-goals-data.styles'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useModulesSubmitModuleResultMutation } from '@app/services/auto-generated/modules'
import errorHandler from '@common/utils/error-handler'
import { ModuleResult } from '@app/services/auto-generated/auth'
import { eventApi } from '@app/services'
import { useAppDispatch } from '@common/hooks'
import { SxProps } from '@mui/material'
import { useLocation } from 'react-router-dom'
import { EVENT_ROUTE } from '@common/constants/routes'

type formType = {
  goal: string[]
  occupation: { status: string; description: string }
  interests: string[]
  about_me: string
}
const CollectGoalsData = (props: { sx?: SxProps; onClose?: () => void }) => {
  const dispatch = useAppDispatch()
  const [currentStepIndex, setCurrentStepIndex] = React.useState(0)
  const { pathname } = useLocation()
  const [submit, { isLoading, isError, error }] = useModulesSubmitModuleResultMutation()
  const { handleSubmit, control, getValues, setValue, watch } = useForm<formType>({
    defaultValues: {
      goal: [],
      occupation: { status: '', description: '' },
      interests: [],
      about_me: '',
    },
  })
  const HandleGoal = (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
    if (event.target.checked) {
      setValue('goal', [...getValues('goal'), value])
    } else {
      setValue(
        'goal',
        getValues('goal').filter((goal: string) => goal !== value),
      )
    }
  }

  const handleOccupation = (value: string) => {
    if (watch('occupation').status !== value) {
      setValue('occupation.status', value)
      setValue('occupation.description', '')
    } else {
      setValue('occupation.status', '')
    }
  }
  const handleInterest = (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
    if (event.target.checked) {
      setValue('interests', [...getValues('interests'), value])
    } else {
      setValue(
        'interests',
        getValues('interests').filter((interest: string) => interest !== value),
      )
    }
  }

  const steps = [
    <Stack key={0} alignItems='center' sx={{ width: '100%' }}>
      <Typography fontWeight={'bold'}>Complete your Profile to continue!</Typography>
      <Stack direction={'row'}>
        <img src={penguinHeadPhoneImg} width={'150px'} />
        <img src={penguinPhoneImg} width={'150px'} />
      </Stack>
      <Typography variant='body2'>
        Your responses will help us connect you with the best speaking partners.
        <br />
        <br />
        It’s quick and easy— just check the boxes that resonate with you!{' '}
      </Typography>
    </Stack>,
    <Step key={1} currentStepIndex={currentStepIndex}>
      <Typography fontWeight={'bold'} variant='body2'>
        My goal is:
      </Typography>
      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              onChange={(e) => HandleGoal(e, 'Improve general communication skills')}
              sx={{ '&:hover': { bgcolor: 'transparent' } }}
              disableRipple
              color='default'
              checkedIcon={<BpCheckedIcon />}
              icon={<BpIcon />}
              name='Improve general communication skills'
            />
          }
          label='To improve my general communication skills'
        />
        <FormControlLabel
          control={
            <Checkbox
              onChange={(e) => HandleGoal(e, 'Make international friends')}
              sx={{ '&:hover': { bgcolor: 'transparent' } }}
              disableRipple
              color='default'
              checkedIcon={<BpCheckedIcon />}
              icon={<BpIcon />}
              name='Make international friends'
            />
          }
          label='To make international friends'
        />
        <FormControlLabel
          control={
            <Checkbox
              onChange={(e) => HandleGoal(e, ' Pass a test')}
              sx={{ '&:hover': { bgcolor: 'transparent' } }}
              disableRipple
              color='default'
              checkedIcon={<BpCheckedIcon />}
              icon={<BpIcon />}
              name=' Pass a test'
            />
          }
          label='To pass a test (IELTS, TOEFL, etc)'
        />
        <FormControlLabel
          control={
            <Checkbox
              onChange={(e) => HandleGoal(e, 'Travel or migrate to another country')}
              sx={{ '&:hover': { bgcolor: 'transparent' } }}
              disableRipple
              color='default'
              checkedIcon={<BpCheckedIcon />}
              icon={<BpIcon />}
              name='Travel or migrate to another country'
            />
          }
          label='To travel or migrate to another country'
        />
        <FormControlLabel
          control={
            <Checkbox
              onChange={(e) => HandleGoal(e, 'Improve English for work')}
              sx={{ '&:hover': { bgcolor: 'transparent' } }}
              disableRipple
              color='default'
              checkedIcon={<BpCheckedIcon />}
              icon={<BpIcon />}
              name='Improve English for work'
            />
          }
          label='To improve my English for work'
        />
      </FormGroup>
    </Step>,
    <Step key={2} currentStepIndex={currentStepIndex}>
      <Typography fontWeight={'bold'} variant='body2'>
        My occupation status is:
      </Typography>
      <FormControl sx={{ pl: 2 }}>
        <RadioGroup>
          <FormControlLabel
            value='employee'
            control={
              <Radio
                disableRipple
                color='default'
                checked={watch('occupation.status') === 'employee'}
                onClick={() => handleOccupation('employee')}
                checkedIcon={<RadioCheckedIcon />}
                icon={<RadioIcon />}
              />
            }
            label='Employee'
          />
          {watch('occupation.status') === 'employee' && (
            <Box sx={{ px: 3 }}>
              <TextField
                control={control}
                name='occupation.description'
                size='small'
                label='Specify what you do'
                variant='outlined'
                fullWidth
              />
            </Box>
          )}

          <FormControlLabel
            value='work'
            control={
              <Radio
                disableRipple
                color='default'
                checked={watch('occupation.status') === 'work'}
                onClick={() => handleOccupation('work')}
                checkedIcon={<RadioCheckedIcon />}
                icon={<RadioIcon />}
              />
            }
            label='Looking for work'
          />
          {watch('occupation.status') === 'work' && (
            <Box sx={{ px: 3 }}>
              <TextField
                control={control}
                name='occupation.description'
                size='small'
                label='Specify what you do'
                variant='outlined'
                fullWidth
              />
            </Box>
          )}

          <FormControlLabel
            value='student'
            control={
              <Radio
                disableRipple
                color='default'
                checked={watch('occupation.status') === 'student'}
                onClick={() => handleOccupation('student')}
                checkedIcon={<RadioCheckedIcon />}
                icon={<RadioIcon />}
              />
            }
            label='Student'
          />
          {watch('occupation.status') === 'student' && (
            <Box sx={{ px: 3 }}>
              <TextField
                control={control}
                name='occupation.description'
                size='small'
                label='Specify what you do'
                variant='outlined'
                fullWidth
              />
            </Box>
          )}

          <FormControlLabel
            value='retired'
            control={
              <Radio
                disableRipple
                color='default'
                checked={watch('occupation.status') === 'retired'}
                onClick={() => handleOccupation('retired')}
                checkedIcon={<RadioCheckedIcon />}
                icon={<RadioIcon />}
              />
            }
            label='Retired'
          />
          {watch('occupation.status') === 'retired' && (
            <Box sx={{ px: 3 }}>
              <TextField
                control={control}
                name='occupation.description'
                size='small'
                label='Specify what you do'
                variant='outlined'
                fullWidth
              />
            </Box>
          )}

          <FormControlLabel
            value='other'
            control={
              <Radio
                disableRipple
                color='default'
                checked={watch('occupation.status') === 'other'}
                onClick={() => handleOccupation('other')}
                checkedIcon={<RadioCheckedIcon />}
                icon={<RadioIcon />}
              />
            }
            label='Other'
          />
          {watch('occupation.status') === 'other' && (
            <Box sx={{ px: 3 }}>
              <TextField
                control={control}
                name='occupation.description'
                size='small'
                label='Specify what you do'
                variant='outlined'
                fullWidth
              />
            </Box>
          )}
        </RadioGroup>
      </FormControl>
    </Step>,
    <Step key={3} currentStepIndex={currentStepIndex}>
      <Typography fontWeight={'bold'} variant='body2'>
        My interests include:
      </Typography>
      <FormGroup sx={{ width: '100%' }}>
        <Grid container sx={{ width: '100%' }}>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  sx={{ '&:hover': { bgcolor: 'transparent' } }}
                  disableRipple
                  color='default'
                  checkedIcon={<BpCheckedIcon />}
                  icon={<BpIcon />}
                  onChange={(e) => handleInterest(e, 'Fitness & Wellness')}
                  name='Fitness & Wellness'
                />
              }
              label='Fitness and Wellness'
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  onChange={(e) => handleInterest(e, 'Art & Design')}
                  sx={{ '&:hover': { bgcolor: 'transparent' } }}
                  disableRipple
                  color='default'
                  checkedIcon={<BpCheckedIcon />}
                  icon={<BpIcon />}
                  name='Art & Design'
                />
              }
              label='Art and Design'
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  sx={{ '&:hover': { bgcolor: 'transparent' } }}
                  disableRipple
                  color='default'
                  checkedIcon={<BpCheckedIcon />}
                  icon={<BpIcon />}
                  onChange={(e) => handleInterest(e, 'Business')}
                  name='Business'
                />
              }
              label='Business'
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  name='Reading & Literature'
                  onChange={(e) => handleInterest(e, 'Reading & Literature')}
                  sx={{ '&:hover': { bgcolor: 'transparent' } }}
                  disableRipple
                  color='default'
                  checkedIcon={<BpCheckedIcon />}
                  icon={<BpIcon />}
                />
              }
              sx={{ display: 'flex', alignItems: 'flex-start' }}
              label='Reading and Literature'
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  sx={{ '&:hover': { bgcolor: 'transparent' } }}
                  disableRipple
                  color='default'
                  checkedIcon={<BpCheckedIcon />}
                  icon={<BpIcon />}
                  name='travel'
                  onChange={(e) => handleInterest(e, 'Travel')}
                />
              }
              label='Travel'
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  sx={{ '&:hover': { bgcolor: 'transparent' } }}
                  disableRipple
                  color='default'
                  checkedIcon={<BpCheckedIcon />}
                  icon={<BpIcon />}
                  name='Science & Technology'
                  onChange={(e) => handleInterest(e, 'Science & Technology')}
                />
              }
              sx={{ display: 'flex', alignItems: 'flex-start' }}
              label='Science and Technology'
            />
          </Grid>
        </Grid>
        <Typography sx={{ mt: 2, mb: 1 }}>Add interests by tags</Typography>
        <Autocomplete
          name='interests'
          multiple
          limitTags={4}
          options={[]}
          defaultValue={[]}
          renderInput={(params) => (
            <TextField {...params} placeholder='Specify what you would enjoy discussing' />
          )}
          freeSolo={true}
          size='small'
          onChange={(event) => {
            if (!watch('interests').includes(event.target.value)) {
              setValue('interests', [...watch('interests'), event.target.value])
            }
          }}
        />
      </FormGroup>
    </Step>,
    <Step key={4} currentStepIndex={currentStepIndex}>
      <Typography fontWeight={'bold'} variant='body2'>
        This is the final one! <br />
        Write a brief introduction about yourself:
      </Typography>
      <TextField
        control={control}
        name='about_me'
        sx={{ pb: 3 }}
        multiline
        minRows={3}
        placeholder='Eg. Hi I’m John, I’m from Korea and I live in the UK. I’m a teacher and I like running and watching football. I’m learning English to meet more people!'
      />
      {isError && (
        <Alert variant='outlined' severity='error' sx={{ mb: 2 }}>
          {errorHandler(error)}
        </Alert>
      )}
    </Step>,
  ]

  const onSubmit: SubmitHandler<formType> = async (formState) => {
    const formData: ModuleResult = {
      id: 'GoalsQuestionnaireModule',
      data: formState,
    }
    await submit({ moduleResult: formData }).unwrap()
    if (pathname.startsWith(EVENT_ROUTE)) {
      dispatch(
        eventApi.util.updateQueryData('eventGetTotalEventJoins', undefined, (draft) => {
          draft.total_event_joins = draft.total_event_joins + 1
          return draft
        }),
      )
    }
  }
  const handleNext = async () => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex((prev) => prev + 1)
    } else if (currentStepIndex === steps.length - 1) {
      await handleSubmit(onSubmit)()
      if (props.onClose) {
        props.onClose()
      }
    }
  }
  return (
    <CenterContainer sx={{ ...props.sx }}>
      <Paper
        elevation={2}
        sx={{ px: 2, py: 4, width: '90%', maxWidth: '470px', minHeight: '370px' }}
      >
        <Stack alignItems='center'>{steps[currentStepIndex]} </Stack>
      </Paper>
      <Button loading={isLoading} variant='contained' onClick={handleNext} sx={{ mt: 5, px: 4 }}>
        Next
      </Button>
    </CenterContainer>
  )
}

const Step = ({
  currentStepIndex,
  children,
}: {
  currentStepIndex: number
  children: React.ReactNode
}) => {
  return (
    <Stack sx={{ width: '100%' }}>
      <Grid container spacing={1}>
        {Array(4)
          .fill(null)
          ?.map((_, index) => (
            <Grid key={index} item xs={3}>
              <Steppper
                sx={{
                  background: (theme) =>
                    currentStepIndex > index ? '#48b7f7' : theme.palette.divider,
                }}
              />
            </Grid>
          ))}
      </Grid>
      <Stack mt={4} spacing={4}>
        {children}
      </Stack>
    </Stack>
  )
}

export default CollectGoalsData
